/**
 * Every panel's four states (idc-portal-design v1.1): loading (skeletons the size of the real content, aria-busy on
 * the card), ready, empty (calm blue: why, and when it changes) and error (what failed, what we did, what happens
 * next). The state message itself is not a live region.
 */
import type { CSSProperties, ReactNode } from "react";
import type { ApiError } from "../api/client.ts";
import { describeError } from "../api/hooks.ts";
import { Icon } from "./Sprites.tsx";

export function StateMessage({ tone, title, children, actions, size }: {
  readonly tone: "idle" | "risk"; readonly title: string; readonly children?: ReactNode; readonly actions?: ReactNode; readonly size?: "sm";
}) {
  return (
    <div className={`ipd-state ipd-state--${tone}${size === "sm" ? " ipd-state--sm" : ""}`}>
      <Icon name={tone === "risk" ? "alert" : "info"} />
      <div>
        <strong>{title}</strong>
        {children ? <p>{children}</p> : null}
        {actions ? <div className="ipd-state-acts">{actions}</div> : null}
      </div>
    </div>
  );
}

/**
 * A panel's error state (AIC-180, as the review queue's): what failed, what we did (nothing out of date is shown) and
 * what happens next (Try again). The failure is announced once through a separate hidden alert.
 */
export function LoadError({ what, error, onRetry, size }: { readonly what: string; readonly error: ApiError | Error; readonly onRetry?: () => void; readonly size?: "sm" }) {
  return (
    <>
      <StateMessage
        tone="risk" size={size} title={`Couldn't load ${what}.`}
        actions={onRetry ? <button type="button" className="ipd-btn" onClick={onRetry}><Icon name="retry" />Try again</button> : undefined}
      >
        {describeError(error)} Nothing out of date is shown, and nothing was changed.
      </StateMessage>
      <p className="ipd-sr" role="alert">{describeError(error)}</p>
    </>
  );
}

export type SkeletonVariant = "line" | "sub" | "pill" | "btn" | "chip";

/** A skeleton block. Always inside an aria-hidden container; the meaning goes in text (sr-only or a caption). */
export function Skeleton({ width, variant = "line", style }: { readonly width?: string; readonly variant?: SkeletonVariant; readonly style?: CSSProperties }) {
  const cls = variant === "line" ? "ipd-sk" : `ipd-sk ipd-sk--${variant}`;
  return <span className={cls} style={{ ...(width ? { width } : null), ...style }} />;
}
